import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Avatar,
    Paper,
    Tabs,
    Tab,
    Dialog,
    IconButton,
    useMediaQuery,
    useTheme,
    Chip
} from '@mui/material';
import {
    GridOn as GridIcon,
    Favorite as FavoriteIcon,
    Comment as CommentIcon,
    PlayArrow as PlayArrowIcon,
    Close as CloseIcon,
    PushPin as PushPinIcon,
    HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import contentService from '../services/contentService';
import ContentCard from '../components/ContentCard';
import { useAuth } from '../context/AuthContext';
import { shareContentLink } from '../utils/shareUtils';
import '../styles/ProtectedRoutes.css';

const ProfilePage = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [selectedPost, setSelectedPost] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const loadPosts = async () => {
        try {
            setLoading(true);
            const response = await contentService.getMyPosts();
            
            if (response.success) {
                // Sort posts: pinned first, then by creation date
                const sortedPosts = response.data.sort((a, b) => {
                    if (a.IsPinned === b.IsPinned) {
                        return new Date(b.CreatedAt) - new Date(a.CreatedAt);
                    }
                    return b.IsPinned - a.IsPinned;
                });
                setPosts(sortedPosts);
            } else {
                toast.error('Failed to load posts');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to load posts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPosts();
    }, []);

    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
    };

    const handlePostClick = (post) => {
        setSelectedPost(post);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedPost(null);
    };

    const handlePinToggle = async (contentId, newPinStatus) => {
        // Update the posts array to reflect the new pin status and re-sort
        setPosts(prevPosts => {
            const updatedPosts = prevPosts.map(post => 
                post.ContentId === contentId 
                    ? { ...post, IsPinned: newPinStatus ? 1 : 0 }
                    : post
            );
            
            // Sort: pinned posts first, then by creation date
            return updatedPosts.sort((a, b) => {
                if (a.IsPinned === b.IsPinned) {
                    return new Date(b.CreatedAt) - new Date(a.CreatedAt);
                }
                return b.IsPinned - a.IsPinned;
            });
        });

        // Also update the selected post if it's the one being pinned
        if (selectedPost && selectedPost.ContentId === contentId) {
            setSelectedPost(prev => ({ ...prev, IsPinned: newPinStatus ? 1 : 0 }));
        }
    };

    const handleDelete = (contentId) => {
        // Remove the deleted post from the list
        setPosts(prevPosts => prevPosts.filter(post => post.ContentId !== contentId));
        
        // Close modal if the deleted post was open
        if (selectedPost && selectedPost.ContentId === contentId) {
            handleCloseModal();
        }
    };

    const handleLike = async (contentId) => {
        if (!user || user.role !== 'Customer') {
            toast.error('Please login as a customer to like posts');
            return;
        }

        try {
            const response = await contentService.toggleLike(contentId);
            if (response.success) {
                setPosts(prev => prev.map(item => 
                    item.ContentId === contentId 
                        ? { 
                            ...item, 
                            LikeCount: response.data.likeCount,
                            IsLikedByUser: response.data.isLiked ? 1 : 0
                        }
                        : item
                ));

                if (selectedPost && selectedPost.ContentId === contentId) {
                    setSelectedPost(prev => ({
                        ...prev,
                        LikeCount: response.data.likeCount,
                        IsLikedByUser: response.data.isLiked ? 1 : 0
                    }));
                }
            }
        } catch (error) {
            toast.error(error.message || 'Failed to like post');
        }
    };

    const handleComment = async (contentId, commentText) => {
        if (!user) {
            toast.error('Please login to comment');
            return;
        }

        try {
            const response = await contentService.addComment(contentId, commentText);
            if (response.success) {
                setPosts(prev => prev.map(item => 
                    item.ContentId === contentId 
                        ? { ...item, CommentCount: parseInt(item.CommentCount) + 1 }
                        : item
                ));

                if (selectedPost && selectedPost.ContentId === contentId) {
                    setSelectedPost(prev => ({
                        ...prev,
                        CommentCount: parseInt(prev.CommentCount) + 1
                    }));
                }

                toast.success('Comment added successfully');
                return true;
            }
        } catch (error) {
            toast.error(error.message || 'Failed to add comment');
            return false;
        }
    };

    const handleShare = async (contentId) => {
        if (!user || user.role !== 'Customer') {
            toast.error('Please login as a customer to share posts');
            return;
        }

        try {
            const response = await contentService.shareContent(contentId, 'link');
            if (response.success) {
                const shareResult = await shareContentLink(contentId, {
                    title: 'Check this post',
                    text: 'Take a look at this post on Shipway'
                });

                setPosts(prev => prev.map(item => 
                    item.ContentId === contentId 
                        ? { ...item, ShareCount: response.data.shareCount }
                        : item
                ));

                if (selectedPost && selectedPost.ContentId === contentId) {
                    setSelectedPost(prev => ({
                        ...prev,
                        ShareCount: response.data.shareCount
                    }));
                }
            }
        } catch (error) {
            toast.error(error.message || 'Failed to share post');
        }
    };

    const handleUploaderClick = (uploaderId) => {
        navigate(`/profile/${uploaderId}`);
    };

    const getMediaUrl = (filePath) => {
        if (!filePath) return '';
        if (filePath.startsWith('http')) return filePath;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${backendUrl}${filePath}`;
    };

    const stats = {
        posts: posts.length,
        approved: posts.filter(p => p.ApprovalStatus === 'approved').length,
        pending: posts.filter(p => p.ApprovalStatus === 'pending').length,
        likes: posts.reduce((sum, post) => sum + (parseInt(post.LikeCount) || 0), 0),
        comments: posts.reduce((sum, post) => sum + (parseInt(post.CommentCount) || 0), 0)
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                    <CircularProgress size={40} className="glass-loading" />
                    <Typography variant="body1" className="glass-text-secondary">
                        Loading profile...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
            {/* Profile Header */}
            <Paper elevation={2} className="glass-card" sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'flex-start' },
                    gap: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 } 
                }}>
                    <Avatar 
                        className="glass-avatar" 
                        sx={{ 
                            width: { xs: 80, sm: 100, md: 120 }, 
                            height: { xs: 80, sm: 100, md: 120 },
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            bgcolor: '#bc965e'
                        }}
                    >
                        {(user?.fullName || user?.name || user?.email)?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    
                    <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' }, width: '100%' }}>
                        <Typography 
                            variant={isMobile ? 'h6' : 'h5'} 
                            className="glass-text-primary" 
                            gutterBottom
                        >
                            {user?.fullName || user?.name || user?.username}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            className="glass-text-secondary" 
                            gutterBottom
                            sx={{ wordBreak: 'break-word' }}
                        >
                            {user?.email}
                        </Typography>
                        
                        {/* Stats */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: { xs: 2, sm: 3, md: 4 }, 
                            mt: 2,
                            justifyContent: { xs: 'center', sm: 'flex-start' },
                            flexWrap: 'wrap'
                        }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                    variant={isMobile ? 'h6' : 'h5'} 
                                    className="glass-text-primary" 
                                    fontWeight="bold"
                                >
                                    {stats.posts}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    className="glass-text-secondary"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                >
                                    Posts
                                </Typography>
                            </Box>
                            {user?.role === 'Customer' && stats.pending > 0 && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        variant={isMobile ? 'h6' : 'h5'} 
                                        className="glass-text-primary" 
                                        fontWeight="bold"
                                        sx={{ color: '#ff9800' }}
                                    >
                                        {stats.pending}
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        className="glass-text-secondary"
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                        Pending
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                    variant={isMobile ? 'h6' : 'h5'} 
                                    className="glass-text-primary" 
                                    fontWeight="bold"
                                >
                                    {stats.likes}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    className="glass-text-secondary"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                >
                                    Likes
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                    variant={isMobile ? 'h6' : 'h5'} 
                                    className="glass-text-primary" 
                                    fontWeight="bold"
                                >
                                    {stats.comments}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    className="glass-text-secondary"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                >
                                    Comments
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Tabs */}
            <Paper elevation={1} className="glass-card" sx={{ mb: 2 }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    centered
                    sx={{
                        '& .MuiTab-root': {
                            color: '#bc965e',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            minHeight: { xs: 48, sm: 56 },
                            '&.Mui-selected': {
                                color: '#bc965e'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#bc965e'
                        }
                    }}
                >
                    <Tab icon={<GridIcon />} label="POSTS" />
                </Tabs>
            </Paper>

            {/* Posts Grid */}
            {posts.length === 0 ? (
                <Paper elevation={1} className="glass-empty-state">
                    <Typography variant="h6" className="glass-text-secondary" gutterBottom>
                        No posts yet
                    </Typography>
                    <Typography variant="body2" className="glass-text-secondary">
                        Create your first post to see it here!
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 0.5
                }}>
                    {posts.map((post) => {
                        const isVideo = post.ContentType === 'video';
                        const isPinned = post.IsPinned === 1;
                        const isPending = post.ApprovalStatus === 'pending';
                        const mediaUrl = getMediaUrl(post.FilePath);
                            
                        return (
                        <Box
                            key={post.ContentId}
                            onClick={() => handlePostClick(post)}
                            sx={{ 
                                position: 'relative',
                                paddingTop: '100%',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                backgroundColor: '#1a1a1a',
                                '&:hover .overlay': {
                                    opacity: 1
                                }
                            }}
                        >
                            {isVideo ? (
                                <Box
                                    component="video"
                                    src={mediaUrl}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                <Box
                                    component="img"
                                    src={mediaUrl}
                                    alt={post.Title || 'Post'}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            )}
                                
                                {isVideo && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 2
                                        }}
                                    >
                                        <PlayArrowIcon sx={{ 
                                            color: 'white', 
                                            fontSize: 20,
                                            filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.8))'
                                        }} />
                                    </Box>
                                )}

                                {isPinned && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            left: 8,
                                            zIndex: 2
                                        }}
                                    >
                                        <PushPinIcon sx={{ 
                                            color: '#ffd700', 
                                            fontSize: 20,
                                            filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.8))'
                                        }} />
                                    </Box>
                                )}

                                {isPending && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 2
                                        }}
                                    >
                                        <Chip 
                                            icon={<PendingIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                                            label="Pending"
                                            size="small"
                                            sx={{ 
                                                backgroundColor: 'rgba(255, 152, 0, 0.9)',
                                                color: 'white',
                                                fontSize: '0.7rem',
                                                height: 20,
                                                '& .MuiChip-icon': {
                                                    marginLeft: '4px'
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                                
                                <Box
                                    className="overlay"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        display: { xs: 'none', sm: 'flex' },
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 2,
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        zIndex: 1
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FavoriteIcon sx={{ color: 'white', fontSize: 18 }} />
                                        <Typography 
                                            variant="body2" 
                                            sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.875rem' }}
                                        >
                                            {post.LikeCount || 0}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CommentIcon sx={{ color: 'white', fontSize: 18 }} />
                                        <Typography 
                                            variant="body2" 
                                            sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.875rem' }}
                                        >
                                            {post.CommentCount || 0}
                                        </Typography>
                                    </Box>
                            </Box>
                        </Box>
                    )})}
                </Box>
            )}

            {/* Post Detail Modal */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            maxHeight: { xs: '100vh', sm: '90vh' },
                            m: { xs: 0, sm: 2 }
                        }
                    }
                }}
            >
                <Box sx={{ position: 'relative', height: '100%' }}>
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            top: { xs: 8, sm: 16 },
                            right: { xs: 8, sm: 16 },
                            zIndex: 1300,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {selectedPost && (
                        <Box sx={{ 
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 0, sm: 2 }
                        }}>
                            <Box sx={{ 
                                width: '100%',
                                maxWidth: { xs: '100%', sm: '425px' }
                            }}>
                                <ContentCard
                                    content={selectedPost}
                                    onLike={handleLike}
                                    onComment={handleComment}
                                    onShare={handleShare}
                                    currentUser={user}
                                    onPinToggle={handlePinToggle}
                                    onUploaderClick={handleUploaderClick}
                                    onDelete={handleDelete}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Dialog>
        </Container>
    );
};

export default ProfilePage;